"use client";

import { Loader2 } from "lucide-react";
import { cn } from "@/modern/utils";
import type { Persona } from "../context/AnalyzeFileContext";
import { ADVISOR_AVATAR_IMAGE_CLASS, getAdvisorTheme } from "../advisorTheme";

type PersonaAvatarProps = {
  persona: Persona;
  size?: "md" | "lg";
  /** Chip row uses ring + halo; inline cards use compact badge. */
  presentation?: "chip" | "inline";
  isSelected?: boolean;
};

export const PersonaAvatar = ({
  persona,
  size = "md",
  presentation = "inline",
  isSelected = false,
}: PersonaAvatarProps) => {
  const Icon = persona.icon;
  const hasAvatar = persona.avatarUrl;
  const theme = getAdvisorTheme(persona.id);
  const dim = size === "lg" ? "w-12 h-12" : "w-11 h-11";
  const iconDim = size === "lg" ? "w-5 h-5" : "w-[1.125rem] h-[1.125rem]";

  if (presentation === "chip") {
    return (
      <div
        className={cn(
          "relative shrink-0 rounded-full",
          dim,
          theme.halo,
          isSelected && theme.glow,
          "transition-shadow duration-200",
        )}
      >
        <div
          className={cn(
            "absolute inset-[3px] overflow-hidden rounded-full",
            "ring-2 ring-offset-2 ring-offset-card",
            theme.ring,
            isSelected && "ring-[2.5px]",
            hasAvatar ? theme.fill : theme.fallback,
          )}
        >
          {hasAvatar ? (
            <img src={persona.avatarUrl!} alt={persona.name} className={ADVISOR_AVATAR_IMAGE_CLASS} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-white">
              <Icon className={iconDim} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden rounded-full ring-2 ring-offset-1 ring-offset-card",
        dim,
        theme.ring,
        hasAvatar ? theme.fill : theme.fallback,
      )}
    >
      {hasAvatar ? (
        <img src={persona.avatarUrl!} alt={persona.name} className={ADVISOR_AVATAR_IMAGE_CLASS} />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-white">
          <Icon className={iconDim} />
        </div>
      )}
    </div>
  );
};

type AdvisorChipProps = {
  persona: Persona;
  insightCount: number;
  isSelected: boolean;
  isGenerating: boolean;
  isLocked: boolean;
  onSelect: () => void;
  size: "md" | "lg";
  /** Single-line labels (pop-out panel). */
  compactLabels?: boolean;
};

export const AdvisorChip = ({
  persona,
  insightCount,
  isSelected,
  isGenerating,
  isLocked,
  onSelect,
  size,
  compactLabels,
}: AdvisorChipProps) => {
  const hasInsights = insightCount > 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isLocked}
      className={cn(
        "group relative flex w-full min-w-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1.5 transition-colors",
        isSelected ? "bg-muted" : "hover:bg-muted/50",
        isLocked && "opacity-50 cursor-not-allowed",
      )}
    >
      <div className="relative">
        <div
          className={cn(
            "rounded-full transition-transform",
            isSelected && "scale-105",
            isGenerating && "opacity-70",
          )}
        >
          <PersonaAvatar persona={persona} size={size} presentation="chip" isSelected={isSelected} />
        </div>
        {isGenerating && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/60">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        )}
        {hasInsights && !isGenerating && (
          <span className="absolute -top-0.5 -right-0.5 z-10 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full border border-card bg-foreground text-background text-[10px] font-semibold flex items-center justify-center leading-none shadow-sm">
            {insightCount}
          </span>
        )}
      </div>
      <span
        className={cn(
          "text-center leading-snug w-full font-medium px-0.5",
          compactLabels
            ? "text-xs whitespace-nowrap"
            : cn("line-clamp-2", size === "lg" ? "text-sm" : "text-xs"),
          isSelected ? "text-foreground font-semibold" : "text-foreground/75",
        )}
      >
        {persona.name}
      </span>
    </button>
  );
};
